import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { WebSocketContext } from "./WebSocketContext";
import { useTheme } from "./contexts/ThemeContext";
import StockCard from "./components/StockCard";
import "./StockList.css"; // Add CSS for the stock list
import BottomSheet from './components/BottomSheet';
import searchIcon from './assets/search.svg';
import {Search, LiveTv, CurrencyRupee, Add, Edit, Save, Cancel, Delete} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Refresh } from '@mui/icons-material';
import { apiAppFetch, checkAppSession } from './utils/apiFetch';

const StockList = () => {
  const { stocks, userScripts, userDetails, lastUpdateTime, netPositions, fetchUserScriptsExternal, removeUserScriptFromList } = useContext(WebSocketContext);
  const { isDarkMode, toggleTheme } = useTheme();
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [filter, setFilter] = useState("MCX FUT"); // Added filter state
  const [showMoneyOptions, setShowMoneyOptions] = useState(false);
  const [proView, setProView] = useState(() => {
    // Load view preference from localStorage
    const savedView = localStorage.getItem('proViewPreference');
    return savedView ? JSON.parse(savedView) : true; // Default to pro view
  });
  const [oneClickTrade, setOneClickTrade] = useState(() => {
    // Load one click trade preference from localStorage
    const savedOneClick = localStorage.getItem('oneClickTradePreference');
    return savedOneClick ? JSON.parse(savedOneClick) : false;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [showTv, setShowTv] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [reorderedScripts, setReorderedScripts] = useState([]);
  const [savedOrder, setSavedOrder] = useState({});
  const draggedItemRef = useRef(null);
  const touchDragIndexRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavigation = (path) => () => {
    navigate(path);
  };

  // Local storage functions for script order
  const saveScriptOrder = (order) => {
    try {
      localStorage.setItem('scriptOrder', JSON.stringify(order));
      console.log('Script order saved to localStorage:', order);
    } catch (error) {
      console.error('Error saving script order:', error);
    }
  };

  const loadScriptOrder = () => {
    try {
      const saved = localStorage.getItem('scriptOrder');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading script order:', error);
      return {};
    }
  };

  const getScriptOrder = (exchange) => {
    return savedOrder[exchange] || [];
  };

  const updateScriptOrder = (exchange, newOrder) => {
    const updatedOrder = {
      ...savedOrder,
      [exchange]: newOrder
    };
    setSavedOrder(updatedOrder);
    saveScriptOrder(updatedOrder);
  };

  // Function to toggle pro view and save preference
  const toggleProView = () => {
    const newProView = !proView;
    setProView(newProView);
    // Save preference to localStorage
    try {
      localStorage.setItem('proViewPreference', JSON.stringify(newProView));
      console.log('View preference saved:', newProView ? 'Pro View' : 'Simple View');
    } catch (error) {
      console.error('Error saving view preference:', error);
    }
  };

  // Function to toggle one click trade and save preference
  const toggleOneClickTrade = () => {
    const newOneClickTrade = !oneClickTrade;
    setOneClickTrade(newOneClickTrade);
    // Save preference to localStorage
    try {
      localStorage.setItem('oneClickTradePreference', JSON.stringify(newOneClickTrade));
      console.log('One Click Trade preference saved:', newOneClickTrade);
    } catch (error) {
      console.error('Error saving one click trade preference:', error);
    }
  };

  // Function to remove script from user's list
  const removeScriptFromUser = async (script) => {
    try {
      const response = await apiAppFetch('removeScriptFromUser', {
        method: 'POST',
        body: JSON.stringify({
          stockId: String(script.lotVariant || '').toUpperCase() === 'MINI'
            ? `${script.instrumentIdentifier}#MINI`
            : script.instrumentIdentifier,
        }),
      });

      if (response.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();

      if (data.success) {
        removeUserScriptFromList(script.instrumentIdentifier, script.lotVariant);
        if (isEditMode) {
          setReorderedScripts((prev) =>
            prev.filter((item) => {
              if (String(item.instrumentIdentifier) !== String(script.instrumentIdentifier)) {
                return true;
              }
              const itemMini = String(item.lotVariant || '').toUpperCase() === 'MINI';
              const scriptMini = String(script.lotVariant || '').toUpperCase() === 'MINI';
              return itemMini !== scriptMini;
            })
          );
        }
      } else {
        alert('Failed to remove script: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error removing script: ' + error.message);
    }
  };

  // Function to handle script deletion
  const handleDeleteScript = (script, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const confirmDelete = window.confirm(
      `Are you sure you want to remove "${script.dispname}" from your watchlist?`
    );
    
    if (confirmDelete) {
      removeScriptFromUser(script);
    }
  };

  const handleToggle = () => {
    setShowTv(prevState => !prevState);
  };

  const handleCardClick = async (stock) => {
    const sessionOk = await checkAppSession();
    if (!sessionOk) {
      return;
    }

    setSelectedStock(stock);
    if(stock.exchange=="OPTSTK"){
      stock.intraday = userDetails.OPTSTK_intraday;
    } else if(stock.exchange=="FUTSTK"){
      stock.intraday = userDetails.FUTSTK_intraday;
    } else if(stock.exchange=="FUTCOM"){
      stock.intraday = userDetails.FUTCOM_intraday;
    }
    setShowBottomSheet(true);
  };

  const handleCloseSheet = () => {
    setShowBottomSheet(false);
    setSelectedStock(null);
    // alert("closed"+showBottomSheet);
  };

  // Function to get the latest stock data for the selected stock
  const getLatestStockData = (stock) => {
    return stocks[stock.instrument_token] || {};
  };

  // Effect to update the selected stock data in the bottom sheet whenever stocks context updates
  useEffect(() => {
    if (selectedStock) {
      const latestStockData = getLatestStockData(selectedStock);
      setSelectedStock((prevStock) => ({
        ...prevStock,
        live: latestStockData,
      }));
    }
  }, [stocks]);

  // Effect to load saved script order on component mount
  useEffect(() => {
    const loadedOrder = loadScriptOrder();
    setSavedOrder(loadedOrder);
  }, []);

  // Effect to listen for edit mode toggle from TitleBar
  useEffect(() => {
    const handleEditModeToggle = () => {
      handleEditMode();
    };

    window.addEventListener('toggleEditMode', handleEditModeToggle);
    return () => {
      window.removeEventListener('toggleEditMode', handleEditModeToggle);
    };
  }, []);

  // Filtered user scripts based on the selected filter and search query
  const filteredUserScripts = Object.values(userScripts).filter((stock) => {
    // Filter by exchange
    let matchesFilter = false;
    if (filter === "MCX FUT") {
      matchesFilter = stock.exchange === "FUTCOM";
    } else if (filter === "NSE FUT") {
      matchesFilter = stock.exchange === "FUTSTK";
    } else if (filter === "NSE OPT") {
      matchesFilter = stock.exchange === "OPTSTK";
    } else {
      matchesFilter = true;
    }
    
    // Filter by search query
    const matchesSearch = searchQuery === "" || 
      (stock.dispname && stock.dispname.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (stock.symbol && stock.symbol.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  // Sort filtered scripts according to saved order
  const getSortedScripts = (scripts) => {
    const exchange = filter === "MCX FUT" ? "FUTCOM" : 
                    filter === "NSE FUT" ? "FUTSTK" : 
                    filter === "NSE OPT" ? "OPTSTK" : "ALL";
    
    const savedOrderForExchange = getScriptOrder(exchange);
    
    if (savedOrderForExchange.length === 0) {
      return [...scripts];
    }

    // Create a map for quick lookup
    const orderMap = {};
    savedOrderForExchange.forEach((scriptId, index) => {
      orderMap[scriptId] = index;
    });

    // Sort scripts according to saved order (copy first — don't mutate source)
    return [...scripts].sort((a, b) => {
      const aOrder = orderMap[a.instrumentIdentifier];
      const bOrder = orderMap[b.instrumentIdentifier];
      
      // If both scripts are in saved order, sort by their order
      if (aOrder !== undefined && bOrder !== undefined) {
        return aOrder - bOrder;
      }
      
      // If only one script is in saved order, prioritize it
      if (aOrder !== undefined) return -1;
      if (bOrder !== undefined) return 1;
      
      // If neither script is in saved order, maintain original order
      return 0;
    });
  };

  const sortedFilteredScripts = getSortedScripts(filteredUserScripts);

  // Use reordered scripts if in edit mode, otherwise use sorted filtered scripts
  const displayScripts = isEditMode && reorderedScripts.length > 0 ? reorderedScripts : sortedFilteredScripts;

  // Reset edit-mode list when filter/search changes (not on every isEditMode toggle)
  useEffect(() => {
    if (isEditMode) {
      setReorderedScripts(getSortedScripts(filteredUserScripts));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchQuery]);

  const positionQtyByStockId = useMemo(() => {
    const map = {};
    (netPositions || []).forEach((position) => {
      const netQty = Number(position.netQty || 0);
      if (!netQty) {
        return;
      }
      const stockId = String(position.stockId || position.instrumentIdentifier || '');
      if (!stockId) {
        return;
      }
      const variant = String(position.lotVariant || 'MEGA').toUpperCase() === 'MINI' ? 'MINI' : 'MEGA';
      map[`${stockId}|${variant}`] = {
        qty: Math.abs(netQty),
        side: position.buyQty > 0 ? 'Buy' : 'Sell',
      };
    });
    return map;
  }, [netPositions]);

  const getOpenPositionForStock = (stock) => {
    const stockId = String(stock?.instrumentIdentifier || '');
    const variant = String(stock?.lotVariant || 'MEGA').toUpperCase() === 'MINI' ? 'MINI' : 'MEGA';
    return stockId ? positionQtyByStockId[`${stockId}|${variant}`] || null : null;
  };

  const handleRefresh = () => {
    fetchUserScriptsExternal();
    //alert("Refresh");
    //refreshTradesAndPortfolio("trades");
  };

  const moveScript = (fromIndex, toIndex) => {
    if (fromIndex === null || fromIndex === undefined || toIndex === null || toIndex === undefined) {
      return;
    }
    if (fromIndex === toIndex) {
      return;
    }

    setReorderedScripts((prev) => {
      const source = prev.length > 0 ? prev : sortedFilteredScripts;
      const newOrder = [...source];
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= newOrder.length ||
        toIndex >= newOrder.length
      ) {
        return prev.length > 0 ? prev : [...sortedFilteredScripts];
      }
      const [draggedScript] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, draggedScript);
      return newOrder;
    });
  };

  const clearDragState = () => {
    draggedItemRef.current = null;
    touchDragIndexRef.current = null;
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Desktop HTML5 drag and drop
  const handleDragStart = (e, index) => {
    draggedItemRef.current = index;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverItem !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragLeave = (e) => {
    // Ignore leave events when moving over child elements
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverItem(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const fromData = e.dataTransfer.getData('text/plain');
    const fromIndex = fromData !== '' ? Number(fromData) : draggedItemRef.current;
    moveScript(fromIndex, dropIndex);
    clearDragState();
  };

  const handleDragEnd = () => {
    clearDragState();
  };

  // Mobile / touch reorder via drag handle
  const getTouchTargetIndex = (clientY) => {
    const items = document.querySelectorAll('.quotes-list .drag-item');
    for (let i = 0; i < items.length; i += 1) {
      const rect = items[i].getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) {
        return i;
      }
    }
    return null;
  };

  const handleTouchStart = (e, index) => {
    if (!isEditMode) {
      return;
    }

    touchDragIndexRef.current = index;
    draggedItemRef.current = index;
    setDraggedItem(index);

    const onTouchMove = (moveEvent) => {
      if (touchDragIndexRef.current === null) {
        return;
      }
      const touch = moveEvent.touches[0];
      if (!touch) {
        return;
      }
      moveEvent.preventDefault();
      const overIndex = getTouchTargetIndex(touch.clientY);
      if (overIndex !== null) {
        setDragOverItem(overIndex);
      }
    };

    const onTouchEnd = (endEvent) => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);

      if (touchDragIndexRef.current === null) {
        return;
      }

      const touch = endEvent.changedTouches && endEvent.changedTouches[0];
      const fromIndex = touchDragIndexRef.current;
      const toIndex = touch ? getTouchTargetIndex(touch.clientY) : null;
      moveScript(fromIndex, toIndex);
      clearDragState();
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
  };

  const handleEditMode = () => {
    if (!isEditMode) {
      setReorderedScripts([...sortedFilteredScripts]);
      setIsEditMode(true);
      return;
    }
    setIsEditMode(false);
    setReorderedScripts([]);
    clearDragState();
  };

  const handleSaveOrder = () => {
    // Save the new order to localStorage
    const exchange = filter === "MCX FUT" ? "FUTCOM" : 
                    filter === "NSE FUT" ? "FUTSTK" : 
                    filter === "NSE OPT" ? "OPTSTK" : "ALL";
    
    const scriptIds = reorderedScripts.map(script => script.instrumentIdentifier);
    updateScriptOrder(exchange, scriptIds);
    
    console.log('Saving new order for', exchange, ':', scriptIds);
    setIsEditMode(false);
    setReorderedScripts([]);
    clearDragState();
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setReorderedScripts([]);
    clearDragState();
  };

  return (
    <div className={`quotes-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {showTv && <div className="video-container">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/4QvoTo1CFPs?si=A2GdO2Akv5IumonS"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div>}

      {/* Control Panel */}
      <div className="control-panel">
        
        
        <div className="toggle-controls">
          <div className="toggle-item">
            <span className="toggle-label">Pro View</span>
            <button 
              className={`toggle-switch ${proView ? 'active' : ''}`}
              onClick={toggleProView}
            />
          </div>
          <div className="action-buttons-container">
            <button 
              className="action-btn add-btn"
              onClick={() => navigate('/addScript')}
              title="Add Script"
            >
              <Add style={{ fontSize: '16px' }} />
              <span>Add</span>
            </button>
            <button 
              className="action-btn edit-btn"
              onClick={handleEditMode}
              title="Edit Scripts"
            >
              <Edit style={{ fontSize: '16px' }} />
              <span>Edit</span>
            </button>
          </div>
        </div>
        
        <div className="search-container">
          <Search style={{ height: '20px', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search by Script Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Quotes Table */}
      <div className="quotes-container">
        {userScripts.length === 0 ? (
          <div className="empty-state">
            <p>Your watchlist is currently empty.</p>
            <p>Please add your desired scripts.</p>
            <button className="add-script-btn" onClick={handleNavigation('/addScript')}>
              Add Script
            </button>
          </div>
        ) : (
          <>
            {/* Filter Tabs */}
            <div className="filter-tabs">
              <button 
                className={`filter-tab ${filter === "MCX FUT" ? 'active' : ''}`}
                onClick={() => setFilter("MCX FUT")}
              >
                MCX FUT
              </button>
              <button 
                className={`filter-tab ${filter === "NSE FUT" ? 'active' : ''}`}
                onClick={() => setFilter("NSE FUT")}
              >
                NSE FUT
              </button>
              <button 
                className={`filter-tab ${filter === "NSE OPT" ? 'active' : ''}`}
                onClick={() => setFilter("NSE OPT")}
              >
                NSE OPT
              </button>
            </div>

            {/* Quotes Table Header */}
            <div className="quotes-table-header" style={{ display: 'none' }}>
              <div className="script-column">Script</div>
              <div className="bid-column">Bid</div>
              <div className="ask-column">Ask</div>
            </div>

            {/* Edit Mode Controls */}
            {isEditMode && (
              <div className="edit-mode-controls">
                <button className="save-btn" onClick={handleSaveOrder}>
                  <Save style={{ height: '16px', marginRight: '8px' }} />
                  Save Order
                </button>
                <button className="cancel-btn" onClick={handleCancelEdit}>
                  <Cancel style={{ height: '16px', marginRight: '8px' }} />
                  Cancel
                </button>
              </div>
            )}

            {/* Quotes List */}
            <div className={`quotes-list ${isEditMode ? 'edit-mode' : ''}`}>
              {displayScripts.map((stock, index) => {
                const _stock = stocks[stock.instrument_token] || {};
                return (
                  <div
                    key={`${stock.instrumentIdentifier}-${String(stock.lotVariant || 'MEGA').toUpperCase()}`}
                    data-drag-index={index}
                    draggable={isEditMode}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`drag-item ${draggedItem === index ? 'dragging' : ''} ${dragOverItem === index ? 'drag-over' : ''}`}
                    style={{ 
                      cursor: isEditMode ? 'move' : 'default',
                      userSelect: isEditMode ? 'none' : 'auto',
                      touchAction: isEditMode ? 'none' : 'auto'
                    }}
                  >
                    {isEditMode && (
                      <>
                        <div 
                          className="drag-handle" 
                          title="Drag to reorder"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onTouchStart={(e) => handleTouchStart(e, index)}
                        >
                          <span>⋮⋮</span>
                        </div>
                        <button
                          className="delete-button"
                          title="Remove from watchlist"
                          onClick={(e) => handleDeleteScript(stock, e)}
                        >
                          <Delete style={{ fontSize: '18px' }} />
                        </button>
                      </>
                    )}
                    <div 
                      style={{ 
                        marginLeft: isEditMode ? '30px' : '0px',
                        marginRight: isEditMode ? '0px' : '0px',
                        transition: 'margin 0.2s ease'
                      }}
                    >
                      <StockCard 
                        _stock={_stock} 
                        stock={stock}
                        openPosition={getOpenPositionForStock(stock)}
                        onCardClick={isEditMode ? () => {} : handleCardClick}
                        isDarkMode={isDarkMode}
                        proView={proView}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Bottom Navigation is handled by NavBar.js */}

      {selectedStock && <BottomSheet isOpen={showBottomSheet} onClose={handleCloseSheet} userDetails={userDetails} stock={selectedStock} />}
    </div>
  );
};

export default StockList;
